import {
    IdcardOutlined,
    DownloadOutlined,
    SearchOutlined,
    EditOutlined,
    ColumnHeightOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Row,
    Col,
    Input,
    Table,
    Select,
    message,
    Modal,
    Tag,
    Checkbox,
    Dropdown,
    Tooltip,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const ALL_COLUMNS = [
    { key: "EmployeeCode", title: "Employee Code", dataIndex: "EmployeeCode", defaultVisible: true },
    { key: "EmployeeName", title: "Employee Name", dataIndex: "EmployeeName", defaultVisible: true },
    { key: "Date", title: "Attendance Date", dataIndex: "Date", defaultVisible: true },
    { key: "PunchIn", title: "Punch-In", dataIndex: "PunchIn", defaultVisible: true },
    { key: "CheckIn", title: "Check-In", dataIndex: "CheckIn", defaultVisible: true },
    { key: "PunchOut", title: "Punch-Out", dataIndex: "PunchOut", defaultVisible: true },
    { key: "CheckOut", title: "Check-Out", dataIndex: "CheckOut", defaultVisible: true },
    { key: "OriginalStatus", title: "Original Status", dataIndex: "status", defaultVisible: true },
    { key: "Status", title: "Status", dataIndex: "AttendanceStatus", defaultVisible: true },
    { key: "status_message", title: "Remarks", dataIndex: "status_message", defaultVisible: true },
    { key: "TotalTime", title: "Total Time", dataIndex: "TotalTime", defaultVisible: true },
    { key: "Organisation", title: "Company Name", dataIndex: "Organisation", defaultVisible: false },
    { key: "Department", title: "Department", dataIndex: "Department", defaultVisible: false },
    { key: "Designation", title: "Designation", dataIndex: "Designation", defaultVisible: false },
    { key: "WorkLocation", title: "Work Location", dataIndex: "WorkLocation", defaultVisible: false },
    { key: "check_in_photo_url", title: "Check-In Photo", dataIndex: "check_in_photo_url", defaultVisible: false },
    { key: "CheckInLocation", title: "Check-In Location", dataIndex: "CheckInLocation", defaultVisible: false },
    { key: "CheckInWorkSite", title: "Check In Site", dataIndex: "CheckInWorkSite", defaultVisible: false },
    { key: "check_out_photo_url", title: "Check-Out Photo", dataIndex: "check_out_photo_url", defaultVisible: false },
    { key: "CheckOutLocation", title: "Check-Out Location", dataIndex: "CheckOutLocation", defaultVisible: false },
    { key: "CheckOutWorkSite", title: "Check Out Site", dataIndex: "CheckOutWorkSite", defaultVisible: false },
    { key: "FirstInType", title: "First In Type", dataIndex: "FirstInType", defaultVisible: false },
    { key: "LastOutType", title: "Last Out Type", dataIndex: "LastOutType", defaultVisible: false },
    { key: "WorkingHours", title: "Working Hours", dataIndex: "OfficeWorkingHours", defaultVisible: true },
    { key: "IsSpecial", title: "Is Special", dataIndex: "is_special", defaultVisible: true }, // ✅
    { key: "IsShift", title: "Is Shift", dataIndex: "is_shift", defaultVisible: true }, // ✅
];

export default function AttendanceHead() {
    const { authData, backUrl } = useContext(AuthContext);
    const isMobile = window.innerWidth < 768;

    // ── Column visibility ─────────────────────────────────────────────────────
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem("attendanceHead_visibleColumns");
        if (saved) { try { return JSON.parse(saved); } catch (_) { } }
        return ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);
    });
    const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem("attendanceHead_visibleColumns", JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    const handleColumnToggle = (key, e) => {
        e.stopPropagation();
        setVisibleColumns((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };
    const handleSelectAll = (e) => { e.stopPropagation(); setVisibleColumns(ALL_COLUMNS.map((c) => c.key)); };
    const handleDeselectAll = (e) => { e.stopPropagation(); setVisibleColumns([]); };
    const handleResetDefault = (e) => { e.stopPropagation(); setVisibleColumns(ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)); };

    const columnSelectorMenu = {
        items: [
            {
                key: "actions",
                label: (
                    <div style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0", marginBottom: 8 }}>
                        <Button size="small" type="link" onClick={handleSelectAll} style={{ color: "#0e497b" }}>Select All</Button>
                        <Button size="small" type="link" onClick={handleDeselectAll} style={{ color: "#0e497b" }}>Deselect All</Button>
                        <Button size="small" type="link" onClick={handleResetDefault} style={{ color: "#0e497b" }}>Reset</Button>
                        <Button
                            size="small" icon={<CloseCircleOutlined />} type="primary"
                            onClick={() => setColumnDropdownOpen(false)}
                            style={{ float: "right", backgroundColor: "white", color: "#0e497b" }}
                        />
                    </div>
                ),
            },
            ...ALL_COLUMNS.map((col) => ({
                key: col.key,
                label: (
                    <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        onChange={(e) => handleColumnToggle(col.key, e)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {col.title}
                    </Checkbox>
                ),
            })),
        ],
    };

    // ── Other state ───────────────────────────────────────────────────────────
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPayroll, setIsPayroll] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [totalRecords, setTotalRecords] = useState(0);

    const [filters, setFilters] = useState({
        emp_code: "", emp_name: "", organisation: "", department: "",
        designation: "", from_date: null, to_date: null, status: "",
    });

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
    const [statusChangeRecord, setStatusChangeRecord] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [statusRemarks, setStatusRemarks] = useState("");
    const [statusSubmitting, setStatusSubmitting] = useState(false);

    const statusOptions = [
        { value: "PRESENT", label: "Present", color: "green" },
        { value: "PERMISSION", label: "Permission", color: "orange" },
        // { value: "ABSENT", label: "Absent", color: "red" },
        // { value: "HOLIDAY", label: "Holiday", color: "blue" },
        { value: "Weekoff", label: "Week Off", color: "purple" },
        { value: "COMPOFF", label: "Comp Off", color: "cyan" },
        { value: "Casual Leave", label: "Casual Leave", color: "magenta" },
        { value: "Sick Leave", label: "Sick Leave", color: "volcano" },
        { value: "Half Day Leave", label: "Half Day", color: "geekblue" },
    ];

    useEffect(() => {
        if (authData?.employee?.user?.username === "BCPL569") setIsPayroll(true);
    }, [authData]);

    const buildApiUrl = (overrideFilters, overridePage) => {
        const f = overrideFilters || filters;
        const p = overridePage || currentPage;
        const params = new URLSearchParams();
        params.append("page", p);
        params.append("page_size", pageSize);
        if (f.emp_code?.trim()) params.append("emp_code", f.emp_code.trim());
        if (f.emp_name?.trim()) params.append("emp_name", f.emp_name.trim());
        if (f.organisation) params.append("organisation", f.organisation);
        if (f.department) params.append("department", f.department);
        if (f.designation) params.append("designation", f.designation);
        if (f.from_date) params.append("from_date", dayjs(f.from_date).format("YYYY-MM-DD"));
        if (f.to_date) params.append("to_date", dayjs(f.to_date).format("YYYY-MM-DD"));
        if (f.status) params.append("status", f.status);
        return `${backUrl}/api/hr/attendance-headoffice/?${params.toString()}`;
    };

    const fetchData = async (overrideFilters, overridePage) => {
        setLoading(true); setDataSource([]); setSelectedRowKeys([]);
        try {
            const res = await axios.get(buildApiUrl(overrideFilters, overridePage), {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const data = res.data.results || res.data;
            setDataSource(data);
            setTotalRecords(res.data.count || data.length);
        } catch {
            message.error("Failed to fetch attendance data");
            setDataSource([]); setTotalRecords(0);
        } finally { setLoading(false); }
    };

    const fetchOrganizations = async () => {
        try { const r = await axios.get(`${backUrl}/api/emp/organisations/`); setOrganisations(r.data.results || r.data); } catch (_) { }
    };
    const fetchDepartments = async (id) => {
        try { const r = await axios.get(`${backUrl}/api/emp/departments/?organisation=${id}`); setDepartments(r.data.results || r.data); } catch (_) { }
    };
    const fetchDesignations = async (id) => {
        try { const r = await axios.get(`${backUrl}/api/emp/designations/?department=${id}`); setDesignations(r.data.results || r.data); } catch (_) { }
    };

    useEffect(() => { fetchOrganizations(); fetchData(); }, []);

    const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    const handleOrganisationChange = (value) => {
        const next = { ...filters, organisation: value, department: "", designation: "" };
        setFilters(next); setDepartments([]); setDesignations([]);
        if (value) fetchDepartments(value);
    };
    const handleDepartmentChange = (value) => {
        const next = { ...filters, department: value, designation: "" };
        setFilters(next); setDesignations([]);
        if (value) fetchDesignations(value);
    };
    const handleDateRangeChange = (dates) => {
        if (dates?.length === 2) setFilters((prev) => ({ ...prev, from_date: dates[0], to_date: dates[1] }));
        else setFilters((prev) => ({ ...prev, from_date: null, to_date: null }));
    };

    const handleApplyFilters = () => { setCurrentPage(1); fetchData(filters, 1); };
    const clearFilters = () => {
        const cleared = { emp_code: "", emp_name: "", organisation: "", department: "", designation: "", from_date: null, to_date: null, status: "" };
        setFilters(cleared); setDepartments([]); setDesignations([]); setCurrentPage(1);
        fetchData(cleared, 1);
    };

    const handleStatusEditClick = (record) => {
        setStatusChangeRecord(record); setNewStatus(record.AttendanceStatus); setStatusRemarks(""); setIsStatusModalVisible(true);
    };
    const handleSubmitStatusChange = async () => {
        if (!statusChangeRecord) return;
        if (!newStatus) { message.warning("Please select a status"); return; }
        if (!statusRemarks.trim()) { message.warning("Please enter remarks"); return; }
        setStatusSubmitting(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/attendance-changes/raise-request/`,
                { attendance_id: statusChangeRecord.id, new_status: newStatus, remarks: statusRemarks.trim() },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success("Status change request submitted successfully!");
            setIsStatusModalVisible(false); setStatusRemarks(""); setNewStatus(""); setStatusChangeRecord(null);
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || "Failed to submit status change request");
        } finally { setStatusSubmitting(false); }
    };
    const handleStatusModalCancel = () => { setIsStatusModalVisible(false); setStatusRemarks(""); setNewStatus(""); setStatusChangeRecord(null); };

    const handleSubmitPermission = async () => {
        if (!selectedRecord) return;
        if (!remarks.trim()) { message.warning("Please enter remarks"); return; }
        setSubmitting(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/attendance-requests/`,
                { attendance_id: selectedRecord.id, employee: selectedRecord.Emp_id, remarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success("Permission consideration submitted successfully!");
            setIsModalVisible(false); setRemarks(""); setSelectedRecord(null);
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.data || error.response?.data?.message || "Failed to submit permission consideration");
        } finally { setSubmitting(false); }
    };
    const handleModalCancel = () => { setIsModalVisible(false); setRemarks(""); setSelectedRecord(null); };

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current); setPageSize(pagination.pageSize);
        fetchData(filters, pagination.current);
    };

    const getStatusColor = (status) => statusOptions.find((o) => o.value === status)?.color || "default";

    // ── Excel Download ────────────────────────────────────────────────────────
    const handleDownloadExcel = () => {
        const rows = selectedRowKeys.length > 0
            ? dataSource.filter((item) =>
                selectedRowKeys.includes(String(item.id || item.EmployeeCode))
            )
            : dataSource;
        if (!rows.length) { message.warning("No data to export!"); return; }

        const activeCols = ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key));

        const excelData = rows.map((row) => {
            const r = {};
            activeCols.forEach((col) => {
                if (col.dataIndex === "check_in_photo_url" || col.dataIndex === "check_out_photo_url") {
                    r[col.title] = row[col.dataIndex] ? "Photo Available" : "-";
                } else if (col.key === "IsSpecial" || col.key === "IsShift") {
                    r[col.title] = row[col.dataIndex] ? "Yes" : "No";
                } else if (col.key === "Status" || col.key === "OriginalStatus") {
                    const v = row["AttendanceStatus"];
                    r[col.title] = v === "Sunday" ? "Week Off"
                        : v === "Present" ? "Original Status - Permission, Changed as Present"
                            : v ?? "-";
                } else {
                    r[col.title] = row[col.dataIndex] ?? "-";
                }
            });
            return r;
        });

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Head Office Attendance");
        XLSX.writeFile(wb, "HeadOffice_Attendance.xlsx");
        message.success("Download started!");
    };

    // ── Build table columns ───────────────────────────────────────────────────
    const getTableColumns = () => {
        const defs = {
            EmployeeCode: { title: "Employee Code", dataIndex: "EmployeeCode", key: "EmployeeCode" },
            EmployeeName: { title: "Employee Name", dataIndex: "EmployeeName", key: "EmployeeName" },

            Date: { title: "Attendance Date", dataIndex: "Date", key: "Date" },
            PunchIn: { title: "Punch-In", dataIndex: "PunchIn", key: "PunchIn" },

            CheckIn: { title: "Check-In", dataIndex: "CheckIn", key: "CheckIn" },
            PunchOut: { title: "Punch-Out", dataIndex: "PunchOut", key: "PunchOut" },

            CheckOut: { title: "Check-Out", dataIndex: "CheckOut", key: "CheckOut" },

            OriginalStatus: { title: "Original Status", dataIndex: "AttendanceStatus", key: "OriginalStatus", width: 200 },
            Status: {
                title: "Status", dataIndex: "AttendanceStatus", key: "Status", width: 200,
                render: (v, record) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Tag color={getStatusColor(v)}>
                            {v === "Sunday" ? "Week Off" : v === "Present" ? "Original Status - Permission, Changed as Present" : v}
                        </Tag>
                        {isPayroll && (
                            <Button type="text" size="small" icon={<EditOutlined />}
                                onClick={() => handleStatusEditClick(record)}
                                style={{ padding: "4px 8px" }} title="Edit Status" />
                        )}
                    </div>
                ),
            },
            status_message: { title: "Remarks", dataIndex: "status_message", key: "status_message", width: 200 },
            TotalTime: { title: "Total Time", dataIndex: "TotalTime", key: "TotalTime" },
            WorkingHours: { title: "Working Hours", dataIndex: "OfficeWorkingHours", key: "WorkingHours" },
            IsSpecial: {
                title: "Is Special",
                dataIndex: "is_special",
                key: "IsSpecial",
                render: (v) => (
                    <Tag color={v ? "green" : "red"}>
                        {v ? "Yes" : "No"}
                    </Tag>
                )
            },
            IsShift: {
                title: "Is Shift",
                dataIndex: "is_shift",
                key: "IsShift",
                render: (v) => (
                    <Tag color={v ? "green" : "red"}>
                        {v ? "Yes" : "No"}
                    </Tag>
                )
            },
            check_in_photo_url: {
                title: "Check-In Photo", dataIndex: "check_in_photo_url", key: "check_in_photo_url",
                render: (url) => url ? <img src={url} alt="Check-In" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }} /> : "-",
            },
            WorkLocation: { title: "Work Location", dataIndex: "WorkLocation", key: "WorkLocation" },

            CheckInLocation: {
                title: "Check-In Location", dataIndex: "CheckInLocation", key: "CheckInLocation",
                render: (text, record) =>
                    record.check_in_latitude && record.check_in_longitude ? (
                        <a href={`https://www.google.com/maps?q=${record.check_in_latitude},${record.check_in_longitude}`}
                            target="_blank" rel="noopener noreferrer" style={{ color: "#1677ff" }}>
                            {text || "View Location"}
                        </a>
                    ) : (text || "-"),
            },
            CheckInWorkSite: {
                title: "Check In Site", dataIndex: "CheckInWorkSite", key: "CheckInWorkSite",
                render: (v, record) => <><p>{v || "-"}</p>{record.onduty_type_check_in}</>,
            },
            check_out_photo_url: {
                title: "Check-Out Photo", dataIndex: "check_out_photo_url", key: "check_out_photo_url",
                render: (url) => url ? <img src={url} alt="Check-Out" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }} /> : "-",
            },
            CheckOutLocation: {
                title: "Check-Out Location", dataIndex: "CheckOutLocation", key: "CheckOutLocation",
                render: (text, record) => {
                    // For auto-checkout records, sales_ fields carry the coords
                    const lat = record.sales_checkout_latitude || record.check_out_latitude;
                    const lng = record.sales_checkout_longitude || record.check_out_longitude;
                    return (
                        <div>
                            {lat && lng ? (
                                <a href={`https://www.google.com/maps?q=${lat},${lng}`}
                                    target="_blank" rel="noopener noreferrer" style={{ color: "#1677ff" }}>
                                    {text || "View Location"}
                                </a>
                            ) : (text || "-")}
                            {record.sales_checkout_remarks && (
                                <div style={{
                                    marginTop: 4, padding: "2px 8px",
                                    background: "#fff7e6", border: "1px solid #ffa940",
                                    borderRadius: 4, fontSize: 11, color: "#d46b08", fontWeight: 500,
                                }}>
                                    ⚠ {record.sales_checkout_remarks}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            CheckOutWorkSite: {
                title: "Check Out Site", dataIndex: "CheckOutWorkSite", key: "CheckOutWorkSite",
                render: (v, record) => <><p>{v || "-"}</p>{record.onduty_type_check_out}</>,
            },
            FirstInType: { title: "First In Type", dataIndex: "FirstInType", key: "FirstInType" },
            LastOutType: { title: "Last Out Type", dataIndex: "LastOutType", key: "LastOutType" },

            Organisation: { title: "Company Name", dataIndex: "Organisation", key: "Organisation" },
            Department: { title: "Department", dataIndex: "Department", key: "Department" },
            Designation: { title: "Designation", dataIndex: "Designation", key: "Designation" },
        };

        return visibleColumns.filter((k) => defs[k]).map((k) => defs[k]);
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        <IdcardOutlined /> Head Office Attendance Records
                    </p>
                ) : (
                    <h5><IdcardOutlined /> Head Office Attendance Records</h5>
                )}
            </center>

            {/* Filters Card */}
            <Card style={{ margin: "20px", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Code</label>
                        <Input placeholder="Enter Employee Code" value={filters.emp_code}
                            onChange={(e) => updateFilter("emp_code", e.target.value)}
                            allowClear onPressEnter={handleApplyFilters} />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Name</label>
                        <Input placeholder="Enter Employee Name" value={filters.emp_name}
                            onChange={(e) => updateFilter("emp_name", e.target.value)}
                            allowClear onPressEnter={handleApplyFilters} />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Company Name</label>
                        <Select placeholder="Select Company" allowClear style={{ width: "100%" }}
                            value={filters.organisation || undefined} onChange={handleOrganisationChange}
                            showSearch filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())}>
                            {organisations.map((org) => <Option key={org.id} value={org.id}>{org.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Department</label>
                        <Select showSearch placeholder="Select Department" allowClear style={{ width: "100%" }}
                            value={filters.department || undefined} onChange={handleDepartmentChange}
                            disabled={!filters.organisation}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())}>
                            {departments.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                        </Select>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Designation</label>
                        <Select showSearch placeholder="Select Designation" allowClear style={{ width: "100%" }}
                            value={filters.designation || undefined} onChange={(v) => updateFilter("designation", v)}
                            disabled={!filters.department}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())}>
                            {designations.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Attendance Status</label>
                        <Select placeholder="Select Status" allowClear style={{ width: "100%" }}
                            value={filters.status || undefined} onChange={(v) => updateFilter("status", v)}>
                            <Option value="PRESENT">Present</Option>
                            <Option value="PERMISSION">Permission</Option>
                            <Option value="ABSENT">Absent</Option>
                            <Option value="HOLIDAY">Holiday</Option>
                            <Option value="Weekoff">Week Off</Option>
                            <Option value="Compoff">Comp Off</Option>
                            <Option value="CASUAL LEAVE">Casual Leave</Option>
                            <Option value="SICK LEAVE">Sick Leave</Option>
                            <Option value="HALFDAY_LEAVE">Half Day</Option>
                            <Option value="LOP">LOP</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={10} lg={6}>
                        <label>Date Range</label>
                        <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY"
                            value={filters.from_date && filters.to_date
                                ? [dayjs(filters.from_date), dayjs(filters.to_date)] : null}
                            onChange={handleDateRangeChange} allowClear />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <label style={{ visibility: "hidden" }}>Action</label>
                        <Button block onClick={clearFilters} style={{ width: "100%" }}>Clear Filters</Button>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <label style={{ visibility: "hidden" }}>Search</label>
                        <Button block type="primary" icon={<SearchOutlined />} onClick={handleApplyFilters}
                            style={{ width: "100%", backgroundColor: "#0e497b" }}>
                            Search
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Action Bar */}
            <div style={{ margin: "0 20px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <Button icon={<DownloadOutlined />}
                    style={{ height: "36px", width: "220px", backgroundColor: "#0e497b", color: "white" }}
                    onClick={handleDownloadExcel} disabled={dataSource.length === 0}>
                    Download {selectedRowKeys.length > 0 ? "Selected" : "All"}
                </Button>

                <Dropdown
                    menu={columnSelectorMenu}
                    trigger={["click"]}
                    placement="bottomRight"
                    open={columnDropdownOpen}
                    onOpenChange={setColumnDropdownOpen}
                >
                    <Tooltip title="Customize Columns">
                        <Button icon={<ColumnHeightOutlined />}>
                            Custom Columns ({visibleColumns.length}/{ALL_COLUMNS.length})
                        </Button>
                    </Tooltip>
                </Dropdown>
            </div>

            {/* Table */}
            <div style={{ margin: "0 20px 20px" }}>
                <Table
                    columns={getTableColumns()}
                    dataSource={dataSource}
                    bordered
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: "max-content" }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                        preserveSelectedRowKeys: true,
                    }}
                    rowKey={(record) => String(record.id || record.EmployeeCode)}
                    onChange={handleTableChange}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </div>

            {/* Permission Modal */}
            <Modal title="Permission Consideration" open={isModalVisible}
                onOk={handleSubmitPermission} onCancel={handleModalCancel}
                confirmLoading={submitting} okText="Submit" cancelText="Cancel" width={600}>
                {selectedRecord && (
                    <div style={{ marginBottom: "20px" }}>
                        <p><strong>Employee:</strong> {selectedRecord.EmployeeName} ({selectedRecord.EmployeeCode})</p>
                        <p><strong>Date:</strong> {selectedRecord.Date}</p>
                        <p><strong>Department:</strong> {selectedRecord.Department}</p>
                    </div>
                )}
                <div>
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Remarks:</label>
                    <TextArea rows={4} placeholder="Enter your remarks here..." value={remarks}
                        onChange={(e) => setRemarks(e.target.value)} maxLength={500} showCount />
                </div>
            </Modal>

            {/* Status Change Modal */}
            <Modal title="Change Attendance Status" open={isStatusModalVisible}
                onOk={handleSubmitStatusChange} onCancel={handleStatusModalCancel}
                confirmLoading={statusSubmitting} okText="Submit Request" cancelText="Cancel" width={600}>
                {statusChangeRecord && (
                    <div style={{ marginBottom: "20px" }}>
                        <p><strong>Employee:</strong> {statusChangeRecord.EmployeeName} ({statusChangeRecord.EmployeeCode})</p>
                        <p><strong>Date:</strong> {statusChangeRecord.Date}</p>
                        <p><strong>Department:</strong> {statusChangeRecord.Department}</p>
                        <p>
                            <strong>Current Status:</strong>{" "}
                            <Tag color={getStatusColor(statusChangeRecord.AttendanceStatus)}>
                                {statusChangeRecord.AttendanceStatus === "Sunday" ? "Week Off" : statusChangeRecord.AttendanceStatus}
                            </Tag>
                        </p>
                    </div>
                )}
                <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                        New Status: <span style={{ color: "red" }}>*</span>
                    </label>
                    <Select placeholder="Select new status" style={{ width: "100%" }}
                        value={newStatus} onChange={(v) => setNewStatus(v)}>
                        {statusOptions.map((opt) => (
                            <Option key={opt.value} value={opt.value}><p>{opt.label}</p></Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                        Remarks: <span style={{ color: "red" }}>*</span>
                    </label>
                    <TextArea rows={4} placeholder="Enter reason for status change..." value={statusRemarks}
                        onChange={(e) => setStatusRemarks(e.target.value)} maxLength={500} showCount />
                </div>
            </Modal>
        </section>
    );
}


